import React, { useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

import { hasData } from "../../../utils/commonUtils";
import { useRequestProcessor } from "../../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../../api/api.keys";
import { GetAudioById, UpdateAlbum } from "../../../api/api.service";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { finalizeMediaUtil, initializeMediaUtil, uploadOnS3 } from "../../../utils/mediaUtils";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { showToast, SUCCESS } from "../../../utils/toastUtils";
import OnboardingForm from "./OnboardingForm";

const UpdateOnboarding = () => {
    const location = useLocation();
    const history = useHistory();
    const [isMediaUpload, setIsMediaUpload] = useState(false);

    const onboardingID = location.pathname.split("/").pop();
    let { useFetch, useMutate } = useRequestProcessor();

    let { mutate, isLoading: isAudioUpdating } = useMutate("", getKey(), (data) =>
        UpdateAlbum(data, onboardingID)
    );

    const { data, isLoading, isFetching } = useFetch(
        getKey([MODULE_KEYS.AUDIO_BY_ID], { onboardingID }),
        async ({ signal }) =>
            await GetAudioById({
                Id: onboardingID
            })
    );

    const updateAudioHandler = async (values) => {
        try {
            setIsMediaUpload(true);

            // Handle media uploads only for fields with new files
            const uploadResults = await Promise.all(
                Object.keys(values).map(async (fieldName) => {
                    const fieldData = values[fieldName];

                    if (fieldData?.file) {
                        const mediaId = await handleMediaUpload(
                            fieldData.file,
                            fieldData?.exactFile
                        );

                        return { fieldName, mediaId };
                    }
                    return null;
                })
            );

            // Attach uploaded media IDs to values
            uploadResults.forEach((result) => {
                if (result) {
                    const { fieldName, mediaId } = result;
                    values[fieldName].mediaId = mediaId;
                }
            });

            // Construct body data using uploaded media IDs or existing ones
            const bodyData = {
                title: values.title,
                description: values.description,
                authorId: Number(values.author?.value),
                categoryId: 5, // Static value
                labelId: Number(values.subCategory?.value),
                narratorId: Number(values.narrator?.value),
                thumbnailId: values.uploadImage?.id
                    ? Number(values.uploadImage?.id)
                    : values.uploadImage?.mediaId?.id,
                croppedThumbnailId: values.uploadThumbnail?.id
                    ? Number(values.uploadThumbnail?.id)
                    : values.uploadThumbnail?.mediaId?.id,
                audios: [
                    {
                        title: values.title,
                        description: values.description,
                        duration: Math.floor(
                            Number(
                                values.sessionUploadAudio?.file?.duration ||
                                    data?.audios[0]?.duration
                            )
                        ),
                        audioMediaId: Number(
                            values.sessionUploadAudio?.mediaId?.id ||
                                data?.audios[0]?.audioMedia?.id
                        ),
                        completionMessage: values.sessionCompletionmessage
                    }
                ]
            };

            // Call mutate with the constructed body data
            mutate(bodyData, {
                onSuccess: (res) => {
                    showToast(SUCCESS, "Onboarding Audio Updated Successfully!");
                    history.push(APP_ROUTES.AUDIO_CONTENT_MANAGEMENT_ONBOARDING);
                    setIsMediaUpload(false);
                },
                onError: (err) => {
                    setIsMediaUpload(false);
                    console.error("Failed to create album:", err);
                }
            });
        } catch (error) {
            setIsMediaUpload(false);
            console.error("Error during form submission:", error);
        }
    };

    // media upload on server
    const handleMediaUpload = async (file, initFile) => {
        return initializeMediaUtil(file).then(async (res) => {
            const credentials = res;
            await uploadOnS3(initFile, credentials);
            return await finalizeMediaUtil(credentials?.mediaId);
        });
    };

    if (isFetching || isLoading || !hasData(data)) {
        return (
            <div style={{ height: "70vh" }}>
                <LoaderFallbackUI isLoader={isFetching} className={"h-100"} />
            </div>
        );
    }

    return (
        <OnboardingForm
            mode="edit"
            isLoading={isAudioUpdating || isMediaUpload}
            data={data}
            onUpdateAudio={updateAudioHandler}
            isDisabled={isMediaUpload}
        />
    );
};

export default UpdateOnboarding;
